// Export your data, and delete your account (GDPR Arts. 20 and 17).
//
// Neither control existed in the UI before this. `authAPI.deleteAccount` and
// `AuthContext.deleteAccount` were wired up but nothing ever called them, and
// the backend route they pointed at did not exist either. Meanwhile /trust
// publicly commits to returning or deleting customer data on request -- so the
// promise was real and the way to act on it was not.
//
// Deletion is a REQUEST plus an immediate lock, not an instant purge: the
// account stops working now and the rows are erased after a grace window, so a
// mis-click is recoverable by contacting support within that window. The copy
// says so, because "deleted" meaning "still recoverable for 30 days" is exactly
// the kind of thing a user should not have to discover afterwards.

import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../api/auth';
import { useToast } from '../../hooks/useToast';
import { logger } from '../../utils/logger';
import Modal from '../Modal/Modal';
import { Button, Input } from '../ui';
import styles from './MFAPanel.module.css';

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function AccountDataPanel() {
  const { deleteAccount } = useAuth();
  const { addToast } = useToast();

  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await authAPI.exportAccountData();
      // Built client-side rather than following the response as a navigation,
      // because the endpoint is bearer-authenticated -- a plain link or
      // window.open would arrive without the Authorization header and 401.
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lazysem-account-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      addToast('Your data has been downloaded', 'success');
    } catch (error) {
      logger.error('Account export failed:', error);
      addToast(errorMessage(error, 'Could not export your data'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await deleteAccount(password);
      const when = result?.deletion_scheduled_for
        ? new Date(result.deletion_scheduled_for).toLocaleDateString()
        : null;
      addToast(
        when
          ? `Your account is locked and will be deleted on ${when}`
          : 'Your account is locked and scheduled for deletion',
        'success',
      );
      // AuthContext has already cleared the session; the router will send them
      // to the login page on the next guarded render.
    } catch (error) {
      // The sole-owner refusal names the organisation to fix, so it is shown
      // verbatim rather than flattened to a generic failure.
      addToast(errorMessage(error, 'Could not delete your account'), 'error');
      setDeleting(false);
    }
  };

  return (
    <section className={styles.panel}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Your data</h2>
          <p className={styles.subtitle}>
            Download a copy of your account data, or close your account for good.
          </p>
        </div>
      </header>

      <div className={styles.body}>
        <p className={styles.line}>
          The export covers your profile, your organisation memberships and your record of
          accepting our terms. It does not include the content you have created in the product
          — email info@lazysem.com and we will put together a complete export for you.
        </p>
        <Button variant="secondary" onClick={handleExport} disabled={exporting} loading={exporting}>
          Download my data
        </Button>
      </div>

      <div className={styles.body}>
        <p className={styles.warning}>
          Deleting your account locks it immediately and removes your data after 30 days. Within
          those 30 days you can still contact us to stop it. After that it cannot be undone.
        </p>
        <Button variant="danger" onClick={() => setDeleteOpen(true)}>
          Delete my account
        </Button>
      </div>

      <Modal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete your account"
      >
        <form
          className={styles.modalForm}
          onSubmit={(e) => {
            e.preventDefault();
            void handleDelete();
          }}
        >
          <p className={styles.line}>
            This locks your account straight away and schedules everything for deletion in 30
            days. If you are the only owner of an organisation that still has other members, you
            will need to hand ownership over first.
          </p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            autoComplete="current-password"
            aria-label="Your password"
          />
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            aria-label="Type DELETE to confirm"
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              // Typing DELETE is friction on purpose. This is the one control on
              // the page whose result the user cannot reverse themselves.
              disabled={deleting || !password || confirmText !== 'DELETE'}
              loading={deleting}
            >
              Delete my account
            </Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}
